/**
 * The gateway strategy in the Flow Optimizer's window (user, 12/10/2026):
 * View tabs Formula (what a good gateway is) · Rules (Not enough data, leave
 * the pool, last resort) · Config (the strategy: hold, alternate, random or
 * split — order and weights by hand or by the formula), and the pool ranked
 * live on the right whatever tab is open. Opens on Config — the strategy is
 * the first decision. The inheritable strategy section's source sits in the header.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { ListChecks, Settings2, Sigma } from 'lucide-react';

import { StepCardGroup } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/api-error';

import type { DropGenPlanDetail } from '../../../../drop-generator.types';
import { HourFormulaCard } from '../../general/hour/formula';
import { InheritTag } from '../../master/InheritTag';
import { DialogSubject } from '../../optimizer-dialog/DialogSubject';
import { OptimizerBody } from '../../optimizer-dialog/OptimizerDialog';
import { RulesCard } from '../../rules/RulesCard';
import type { ContainerEditor } from '../../state/useContainerEditor';
import { GatewayVerdictCard } from '../../verdicts/cards';
import { StrategyConfig } from '../StrategyConfig';
import { formulaInUse, poolRouteIds } from './formula-pool';
import { RankedRoutes } from './RankedRoutes';
import { type GatewayRankingQuery, useGatewayRanking } from './use-gateway-ranking';

const HINT =
  "Like the Flow Optimizer: each date range has its own formula over the tracker's numbers for each gateway (grouped by the gateway the drop went out on). Rates (CR, EPV, ROI…) compare gateways fairly; counts mostly favour the gateway used most. The “Not enough data” rule (Rules) reads the first range.";

const Notice: FC<{ children: string }> = ({ children }) => (
  <p className="rounded-lg border border-dashed border-border px-4 py-4 text-sm text-muted-foreground">{children}</p>
);

interface Props {
  editor: ContainerEditor;
  plan: DropGenPlanDetail;
  containerId: string | null;
  countryKey: string;
  splitOver: number;
  routeName: (routeId: number) => string;
  onClose: () => void;
}

export const GatewayStrategyContent: FC<Props> = ({ editor, plan, containerId, countryKey, splitOver, routeName, onClose }) => {
  const { draft, gatewayOp } = editor;
  const gw = draft.gateway;
  const set = (patch: Partial<typeof gw.ranking>) => gatewayOp({ kind: 'ranking', ranking: { ...gw.ranking, ...patch } });
  // Always read while the pop-up is open — the right panel shows the ranking even before the formula is in use.
  const query = useGatewayRanking({ countryKey, campaignWiserId: draft.campaignWiserId, ranking: gw.ranking, routeIds: poolRouteIds(gw), enabled: true });
  return (
    <OptimizerBody
      title="Gateway strategy"
      subtitle={<DialogSubject editor={editor} />}
      headerAside={<InheritTag section="gatewayStrategy" />}
      defaultTab="config"
      tabs={[
        { value: 'formula', label: 'Formula', icon: Sigma, content: <HourFormulaCard formula={gw.ranking.formula} onChange={(formula) => set({ formula })} hint={HINT} /> },
        { value: 'rules', label: 'Rules', icon: ListChecks, content: <RulesCard surface="gateways" rules={gw.ranking.rules} onChange={(rules) => set({ rules })} firstRange={gw.ranking.formula.groups[0]} /> },
        {
          value: 'config',
          label: 'Config',
          icon: Settings2,
          content: (
            <div className="flex flex-col gap-4">
              <StrategyConfig gateway={gw} splitOver={splitOver} ranking={query.data} onOp={gatewayOp} />
              <StepCardGroup>
                <GatewayVerdictCard index={1} editor={editor} plan={plan} containerId={containerId} />
              </StepCardGroup>
            </div>
          ),
        },
      ]}
      panel={<LivePanel editor={editor} query={query} routeName={routeName} />}
      panelWidthClass="lg:w-[46rem]"
      onClose={onClose}
    />
  );
};

const LivePanel: FC<{ editor: ContainerEditor; query: GatewayRankingQuery; routeName: (routeId: number) => string }> = ({ editor, query, routeName }) => {
  const [open, setOpen] = useState<string | null>(null);
  const gw = editor.draft.gateway;
  if (gw.pool.length === 0) return <Notice>Add gateways first to rank them.</Notice>;
  if (query.isError) return <Notice>{`Ranking unavailable — ${getApiErrorMessage(query.error)}. The day uses the pool's own order and equal shares meanwhile.`}</Notice>;
  if (!query.data) return <Notice>{query.isFetching ? 'Ranking the gateways…' : 'Pick a campaign to rank its gateways.'}</Notice>;
  return (
    <div className="flex flex-col gap-2">
      {formulaInUse(gw) ? null : <p className="text-[11px] text-muted-foreground">Ranked on the formula. The day's order and shares follow these scores once Config picks “Formula ranking” or “By formula”; the rules act either way.</p>}
      <RankedRoutes gateway={gw} data={query.data} routeName={routeName} open={open} onOpen={(id) => setOpen(open === id ? null : id)} loading={query.isFetching || query.pending} />
    </div>
  );
};
